import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from '../../environments/environment';

/**
 * Application-wide error handler.
 *
 * Angular calls handleError() for:
 *  - Uncaught exceptions thrown inside components, pipes, directives
 *  - Errors thrown inside lifecycle hooks (ngOnInit, etc.)
 *  - Errors that bubble up through the change-detection cycle
 *
 * HTTP errors (4xx / 5xx) are NOT routed here — those come through the
 * HttpClient observable chain and are handled in Step 7's HTTP interceptor.
 *
 * Execution order on error:
 *  1. Strip Angular's WrappedError wrapper to get the original exception
 *  2. Send to Sentry (only when enabled — i.e. production builds with DSN set)
 *  3. Log structured details to the browser console for local debugging
 *  4. In production, show the Sentry user-feedback dialog so users can
 *     describe what they were doing when the error occurred
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private zone: NgZone) {}

  handleError(error: unknown): void {
    // Angular sometimes wraps errors in a context object; unwrap to the real cause.
    const unwrapped = (error as { ngOriginalError?: unknown })?.ngOriginalError ?? error;

    // NG0100 (ExpressionChangedAfterItHasBeenCheckedError) is a dev-mode-only
    // diagnostic that never fires in production. It does not affect
    // functionality — downgrade to a warning and skip Sentry.
    const message = unwrapped instanceof Error ? unwrapped.message : String(unwrapped);
    if (message.includes('NG0100') || message.includes('ExpressionChangedAfterItHasBeenChecked')) {
      console.warn('[NG0100 suppressed]', message);
      return;
    }

    // Always report to Sentry first — do this before any logging so the event
    // is captured even if subsequent code throws.
    Sentry.captureException(unwrapped);

    // Structured console output so developers get a clear picture in DevTools.
    // In production this is still useful for on-call debugging via remote DevTools.
    const stack   = unwrapped instanceof Error ? unwrapped.stack  : undefined;

    console.error('[GlobalErrorHandler]', { message, stack, raw: unwrapped });

    // Show Sentry's user-feedback dialog in production only.
    // This lets users describe what they were doing — invaluable context for
    // hard-to-reproduce bugs. Suppressed in dev to avoid dialog noise.
    if (environment.production) {
      this.zone.run(() => {
        Sentry.showReportDialog();
      });
    }
  }
}
